import { useCallback, useEffect, useState } from "react";
import { Copy, ExternalLink, MousePointerClick, Users, WalletCards, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

function money(value) {
  return `${Number(value || 0).toFixed(2).replace(".", ",")} €`;
}

export default function Affiliate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setData((await api.get("/affiliate/me")).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Espace affilié indisponible."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-muted-foreground">Chargement…</div>;
  if (!data) return null;
  const shareUrl = `${window.location.origin}${data.affiliate.share_path}`;

  const copy = async () => {
    await navigator.clipboard.writeText(shareUrl);
    toast.success("Lien affilié copié.");
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.16em] text-primary">Partenariat Harf</div>
        <h1 className="mt-1 font-head text-3xl font-bold">Mon espace affilié</h1>
        <p className="mt-2 text-muted-foreground">Tu reçois 50 % du montant des plans payants choisis par les personnes inscrites via ton lien.</p>
      </div>

      <section className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-sm font-semibold">Ton lien à partager</div>
            <div className="mt-2 break-all rounded-xl border bg-background px-3 py-2 font-mono text-sm">{shareUrl}</div>
            <div className="mt-2 text-xs text-muted-foreground">Code partenaire : {data.affiliate.code} · Commission : {data.affiliate.rate * 100}%</div>
          </div>
          <button onClick={copy} className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground">
            <Copy size={16} /> Copier le lien
          </button>
        </div>
        {!data.provider_connected && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-200">
            <ShieldCheck size={16} className="mt-0.5 shrink-0" />
            Les commissions payantes sont calculées et affichées, mais leur versement sera activé après la connexion de Stripe.
          </div>
        )}
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        {[
          [MousePointerClick, "Clics sur le lien", data.metrics.clicks],
          [Users, "Inscriptions", data.metrics.signups],
          [WalletCards, "À recevoir ce mois", money(data.metrics.this_month_pending_eur)],
        ].map(([Icon, label, value]) => (
          <div key={label} className="rounded-2xl border bg-card p-5">
            <Icon size={19} className="mb-3 text-primary" />
            <div className="font-head text-2xl font-bold">{value}</div>
            <div className="text-sm text-muted-foreground">{label}</div>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border bg-card overflow-hidden">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h2 className="font-head text-xl font-bold">Personnes inscrites via ton lien</h2>
            <p className="text-xs text-muted-foreground">Les inscriptions gratuites et les périodes d’essai sont également visibles.</p>
          </div>
          <a href={shareUrl} target="_blank" rel="noreferrer" className="hidden items-center gap-1 text-xs font-semibold text-primary sm:flex"><ExternalLink size={14} /> Ouvrir</a>
        </div>
        {data.conversions.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground">Aucune inscription pour le moment.</div>
        ) : (
          <div className="divide-y">
            {data.conversions.map((item) => (
              <div key={item.referred_user_id} className="grid gap-2 px-5 py-4 sm:grid-cols-[1.4fr_0.8fr_0.8fr_0.8fr] sm:items-center">
                <div>
                  <div className="font-medium">{item.email}</div>
                  <div className="text-xs text-muted-foreground">{new Date(item.registered_at).toLocaleDateString("fr-FR")}</div>
                </div>
                <div className="text-sm"><span className="text-xs text-muted-foreground">Compte</span><br />{item.account_type} · {item.plan}</div>
                <div className="text-sm"><span className="text-xs text-muted-foreground">Commission</span><br />{money(item.commission_eur)}</div>
                <div className={`text-xs font-semibold ${item.payment_status === "pending_stripe" ? "text-amber-600" : "text-muted-foreground"}`}>
                  {item.payment_status === "pending_stripe" ? "En attente Stripe" : "Démo / gratuit"}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}